import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

import {
  findAuthorizedStaffByEmail,
  signIn as signInRepository,
  signOut as signOutRepository,
  syncStaffRole as syncStaffRoleRepository,
  updatePassword as updatePasswordRepository,
} from "@/core/repositories/auth.repository";
import { getSupabaseClient } from "@/lib/supabase";
import {
  type StaffRole,
} from "@/types/auth";

type SignInServiceResult = {
  success: boolean;
  role?: StaffRole;
  error?: string;
};

type SetPasswordServiceResult = {
  success: boolean;
  error?: string;
};

const signInSchema = z.object({
  email: z.email("Invalid email format."),
  password: z.string().min(6, "Password must contain at least 6 characters."),
});

const setPasswordSchema = z
  .object({
    password: z.string().min(6, "Password must contain at least 6 characters."),
    confirmPassword: z.string().min(1, "Please confirm your password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

const allowedStaffRoles = ["admin", "editor"] as const satisfies readonly StaffRole[];

const ACCESS_DENIED_ERROR = "Access Denied: Restricted area.";
const ACCOUNT_INACTIVE_ERROR = "Account inactive.";

const resolveStaffRoleFromTeam = async (): Promise<
  | { role: StaffRole }
  | { error: "inactive" | "denied" }
> => {
  const synced = await syncStaffRoleRepository();

  if (synced.success && synced.role) {
    return { role: synced.role };
  }

  if (synced.error === "inactive") {
    return { error: "inactive" };
  }

  return { error: "denied" };
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY");
}

const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const checkIsAuthorizedStaff = async (
  email: string,
): Promise<{ id: string } | null> => {
  return findAuthorizedStaffByEmail(email.trim().toLowerCase());
};

export const signIn = async (
  email: string,
  password: string,
): Promise<SignInServiceResult> => {
  const parsed = signInSchema.safeParse({ email, password });

  if (!parsed.success) {
    return {
      success: false,
      error: parsed.error.issues[0]?.message ?? "Invalid credentials.",
    };
  }

  const normalizedEmail = parsed.data.email.trim().toLowerCase();
  const result = await signInRepository(normalizedEmail, parsed.data.password);

  if (!result.success) {
    return {
      success: false,
      error: result.error ?? "Sign in failed.",
    };
  }

  const roleResult = await resolveStaffRoleFromTeam();

  if ("error" in roleResult) {
    await supabase.auth.signOut();
    return {
      success: false,
      error:
        roleResult.error === "inactive"
          ? ACCOUNT_INACTIVE_ERROR
          : ACCESS_DENIED_ERROR,
    };
  }

  if (!allowedStaffRoles.includes(roleResult.role)) {
    await supabase.auth.signOut();
    return {
      success: false,
      error: ACCESS_DENIED_ERROR,
    };
  }

  const staffRecord = await checkIsAuthorizedStaff(normalizedEmail);

  if (!staffRecord) {
    await supabase.auth.signOut();
    return {
      success: false,
      error: ACCESS_DENIED_ERROR,
    };
  }

  return {
    success: true,
    role: roleResult.role,
  };
};

export const setPassword = async (
  password: string,
  confirmPassword: string,
): Promise<SetPasswordServiceResult> => {
  const parsed = setPasswordSchema.safeParse({ password, confirmPassword });

  if (!parsed.success) {
    return {
      success: false,
      error: parsed.error.issues[0]?.message ?? "Invalid password.",
    };
  }

  const {
    data: { user },
    error: userError,
  } = await getSupabaseClient().auth.getUser();

  if (userError || !user?.email) {
    return {
      success: false,
      error: "Your session has expired. Please open the invitation link again.",
    };
  }

  const staffRecord = await checkIsAuthorizedStaff(user.email);

  if (!staffRecord) {
    await signOutRepository();
    return {
      success: false,
      error: ACCESS_DENIED_ERROR,
    };
  }

  const result = await updatePasswordRepository(parsed.data.password);

  if (!result.success) {
    return {
      success: false,
      error: result.error ?? "Failed to set password.",
    };
  }

  await syncStaffRoleRepository();

  return { success: true };
};

export const ensureAuthorizedStaffSession = async (): Promise<boolean> => {
  const {
    data: { user },
    error: userError,
  } = await getSupabaseClient().auth.getUser();

  if (userError || !user?.email) {
    return false;
  }

  const staffRecord = await checkIsAuthorizedStaff(user.email);

  if (!staffRecord) {
    await signOutRepository();
    return false;
  }

  const synced = await syncStaffRoleRepository();
  return synced.success && Boolean(synced.role);
};
